import { useState } from "react";
import { E_LANDING_PAGE_TYPE } from "@/utilities/types/LandingPage";
import { useNavigate } from "react-router-dom";
import { routes } from "@/config/routes";
import { CustomizeDomainState } from "../../grid-listing/types";

interface DnsGuideData {
  customDomain: string;
  cNameTargetDomain: string;
  cNameTarget: string;
  originalState?: CustomizeDomainState; // Store the original domain state
}

interface UseDnsGuideModalProps {
  afterClose?: () => void; // Callback after closing (e.g., navigate)
}

/**
 * Custom hook to manage state and logic for DNS Configuration Guide modal
 */
export function useDnsGuideModal({ afterClose }: UseDnsGuideModalProps = {}) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [guideData, setGuideData] = useState<DnsGuideData | null>(null);

  /**
   * Open DNS Guide modal and set data
   */
  const openDnsGuide = (data: DnsGuideData) => {
    setGuideData(data);
    setIsOpen(true);
  };

  /**
   * Close DNS Guide modal and clear data
   */
  const closeDnsGuide = () => {
    setIsOpen(false);
    setGuideData(null);
    afterClose?.();
  };

  /**
   * Handle event when user completes DNS configuration
   */
  const handleDone = () => {
    setIsOpen(false);
    setGuideData(null);
    navigate(routes.landingPages.listing);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  /**
   * Handle after updating or creating a domain
   * Will open DNS Guide modal if it's a custom domain
   */
  const handleDomainSubmit = (
    domain: string,
    type: E_LANDING_PAGE_TYPE,
    cNameTargetDomain?: string,
    originalState?: CustomizeDomainState,
    cNameTarget?: string
  ) => {
    if (type === E_LANDING_PAGE_TYPE.CUSTOM) {
      openDnsGuide({
        customDomain: domain,
        cNameTargetDomain: cNameTargetDomain || "",
        originalState,
        cNameTarget: cNameTarget || "",
      });
      return true; // DNS Guide opened
    }
    return false; // DNS Guide not opened (subdomain)
  };

  return {
    isOpen,
    guideData,
    openDnsGuide,
    closeDnsGuide,
    handleDone,
    handleDomainSubmit,
    handleClose,
    handleOpen,
  };
}
